import { useQuery } from '@tanstack/react-query';
import { api, type DestinationOption } from '../lib/api';
import { Input } from './ui/input';
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export interface DestinationValue {
  destinationType: string;
  destinationId: string | null;
  destinationValue: string | null;
}

/**
 * The single destination picker.
 *
 * Reused by inbound routes, IVR options, ring-group failover, queue failover, parking timeout and
 * extension forwarding. Building one component well matters more than any other UI decision here:
 * every place a call can be routed uses the same vocabulary, so an admin learns it once.
 *
 * It emits exactly the (type, id, value) triple the API and the flow interpreter both speak.
 */
export function DestinationPicker({
  value,
  onChange,
  label = 'Destination',
  /** Excludes a node from its own options, so an IVR cannot be its own timeout target. */
  excludeId,
}: {
  value: DestinationValue;
  onChange: (next: DestinationValue) => void;
  label?: string | undefined;
  excludeId?: string | undefined;
}) {
  const { data: options = [], isLoading } = useQuery({
    queryKey: ['destinations'],
    queryFn: () => api.get<DestinationOption[]>('/api/v1/destinations'),
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () =>
      api.get<Array<{ id: string; number: string; name: string }>>('/api/v1/extensions'),
  });

  const grouped = [
    {
      group: 'Extensions',
      items: extensions.map((e) => ({
        type: 'extension',
        id: e.id,
        label: `${e.number} — ${e.name}`,
      })),
    },
    ...['ivr', 'ring_group', 'queue', 'conference', 'park'].map((type) => ({
      group: GROUP_LABELS[type] ?? type,
      items: options
        .filter((o) => o.type === type && o.id !== excludeId)
        .map((o) => ({
          type: o.type,
          id: o.id,
          label: o.number ? `${o.number} — ${o.label}` : o.label,
        })),
    })),
  ].filter((g) => g.items.length > 0);

  const selectValue =
    value.destinationType === 'hangup'
      ? 'hangup'
      : value.destinationType === 'external'
        ? 'external'
        : value.destinationId
          ? `${value.destinationType}:${value.destinationId}`
          : '';

  return (
    <div className="space-y-2">
      <label className="block space-y-1.5">
        <span className="text-sm font-medium">{label}</span>
        <ShadcnSelect
          value={selectValue === '' ? '__none__' : selectValue}
          disabled={isLoading}
          onValueChange={(raw) => {
            if (raw === 'hangup' || raw === '__none__') {
              onChange({ destinationType: 'hangup', destinationId: null, destinationValue: null });
              return;
            }
            if (raw === 'external') {
              onChange({ destinationType: 'external', destinationId: null, destinationValue: '' });
              return;
            }
            const [type, id] = raw.split(':');
            onChange({
              destinationType: type ?? 'hangup',
              destinationId: id ?? null,
              destinationValue: null,
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a destination…" />
          </SelectTrigger>
          <SelectContent>
            {/* Grouped, because a PBX of any size has more destinations than fit in a flat list
                and "which queue was that" is easier to answer under a heading. */}
            {grouped.map((group) => (
              <SelectGroup key={group.group}>
                <SelectLabel>{group.group}</SelectLabel>
                {group.items.map((item) => (
                  <SelectItem key={`${item.type}:${item.id}`} value={`${item.type}:${item.id}`}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
            <SelectGroup>
              <SelectLabel>Other</SelectLabel>
              <SelectItem value="external">External number…</SelectItem>
              <SelectItem value="hangup">Hang up</SelectItem>
            </SelectGroup>
          </SelectContent>
        </ShadcnSelect>
      </label>

      {value.destinationType === 'external' ? (
        <Input
          placeholder="Number to dial, e.g. +441234567890"
          value={value.destinationValue ?? ''}
          onChange={(e) => onChange({ ...value, destinationValue: e.target.value })}
        />
      ) : null}
    </div>
  );
}

const GROUP_LABELS: Record<string, string> = {
  ivr: 'IVR menus',
  ring_group: 'Ring groups',
  queue: 'Queues',
  conference: 'Conference rooms',
  park: 'Parking lots',
};
